/**
 * The pure half of the mobile control plane client (ADR-029): response
 * validation and the gate decision, with no imports at all — so the root test
 * suite can exercise the actual decision logic the way tests/inbox does for
 * inbox-sync, rather than trusting a structural scan.
 *
 * Everything device-bound (fetch, storage, Platform, Constants) lives in
 * release-status.ts, which delegates here.
 */

export type UpdateStatus = "none" | "recommended" | "required";

export type ReleaseStatus = {
  flags: Record<string, unknown>;
  update: { status: UpdateStatus; rolloutPercent: number };
  ota: { runtimeVersion: string | null; rolloutPercent: number } | null;
};

export type GateVerdict = {
  /** Render the blocking update screen instead of the app. */
  blocked: boolean;
  /** Show the dismissible "update available" prompt. */
  prompt: boolean;
};

export const NO_VERDICT: GateVerdict = { blocked: false, prompt: false };

/**
 * Validate the server's answer. Anything that is not exactly the expected
 * shape returns null, and every caller treats null as "no answer": flags keep
 * their defaults, the gate shows nothing, OTA does nothing. The OTA field is
 * additionally validated on its own — a malformed `ota` inside an otherwise
 * valid response degrades to null (fail closed) rather than discarding the
 * flags and gate that came with it.
 */
export function parseReleaseStatus(raw: unknown): ReleaseStatus | null {
  if (typeof raw !== "object" || raw === null) return null;
  const { flags, update, ota } = raw as Record<string, unknown>;

  if (typeof flags !== "object" || flags === null || Array.isArray(flags)) {
    return null;
  }
  if (
    typeof update !== "object" ||
    update === null ||
    !isStatus((update as Record<string, unknown>).status) ||
    !isPercent((update as Record<string, unknown>).rolloutPercent)
  ) {
    return null;
  }

  let otaOut: ReleaseStatus["ota"] = null;
  if (typeof ota === "object" && ota !== null) {
    const o = ota as Record<string, unknown>;
    if (
      (typeof o.runtimeVersion === "string" || o.runtimeVersion === null) &&
      isPercent(o.rolloutPercent)
    ) {
      otaOut = {
        runtimeVersion: o.runtimeVersion,
        rolloutPercent: o.rolloutPercent,
      };
    }
  }

  return {
    flags: flags as Record<string, unknown>,
    update: update as ReleaseStatus["update"],
    ota: otaOut,
  };
}

function isStatus(v: unknown): v is UpdateStatus {
  return v === "none" || v === "recommended" || v === "required";
}

function isPercent(v: unknown): v is number {
  return typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 100;
}

/** What this device knows that the server must not: platform, bucket, memory. */
export type GateContext = {
  /** Platform.OS — "web" never gates (a browser deploy updates instantly). */
  platform: string;
  /** The stable local rollout bucket, 0–99. */
  bucket: number;
  /** The build the user last dismissed the prompt on, or null. */
  dismissedBuild: string | null;
  /** This binary's build number, or null (web, or unknown). */
  ownBuild: string | null;
};

/**
 * Turn the server's answer into what this device shows. The server decides
 * WHO is affected (status + ramp threshold); the device holds only its bucket
 * and its remembered dismissal.
 *
 * Fails open: a null status (failed fetch, malformed body) can never block.
 * "Required" ignores the bucket — the server already guarantees it only
 * targets fully-available versions, and a partial blocking screen is the
 * failure mode PLAN.md §2.5 exists to prevent.
 */
export function decideGate(
  status: ReleaseStatus | null,
  ctx: GateContext,
): GateVerdict {
  if (!status || ctx.platform === "web") return NO_VERDICT;

  if (status.update.status === "required") {
    return { blocked: true, prompt: false };
  }

  if (status.update.status === "recommended") {
    if (ctx.bucket >= status.update.rolloutPercent) return NO_VERDICT;
    // Dismissal is remembered per installed build: dismissing once silences
    // the prompt until the user actually updates (a prompt, not a nag).
    if (ctx.dismissedBuild !== null && ctx.dismissedBuild === ctx.ownBuild) {
      return NO_VERDICT;
    }
    return { blocked: false, prompt: true };
  }

  return NO_VERDICT;
}
